import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { cn } from "@/common/utils/cn";
import {
  MaintenanceForwardingSectionStyle,
  maintenanceForwardingSectionStyles,
} from "@/features/maintenance/features/workflows/constants/forwardings/maintenanceForwardingSectionStyles.constant";
import { fiscalAssessmentDocumentGroupsKind } from "@/features/maintenance/features/workflows/constants/forwardings/steps/fiscalAssessmentEntry/fiscalAssessmentDocumentGroupsKind.constant";
import { useForwardingTabContext } from "@/features/maintenance/features/workflows/hooks/forwardings/useForwardingTabContext";
import { MaintenanceFiscalAssessmentEntryStatus } from "@/features/maintenance/features/workflows/types/forwardings/fiscalAssessmentEntry/fiscalAssessmentEntry.type";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FiscalAssessmentDocumentGroupDocument from "./FiscalAssessmentDocumentGroupDocument";

interface MaintenanceWorkflowForwardingCheckinEntrySectionProps { }

const MaintenanceWorkflowForwardingFiscalAssessmentEntrySection = ({ }: MaintenanceWorkflowForwardingCheckinEntrySectionProps) => {
  const { forwarding, forwardingUpdatedAt } = useForwardingTabContext();

  const { id: workflowId } = useParams();
  const navigate = useNavigate();

  const isAwaitingEstablishmentDocuments = useMemo(() => {
    return forwarding.fiscal_assessment_entry?.status === MaintenanceFiscalAssessmentEntryStatus.AwaitingEstablishmentDocuments;
  }, [forwardingUpdatedAt]);

  const isAwaitingManagementReview = useMemo(() => {
    return forwarding.fiscal_assessment_entry?.status === MaintenanceFiscalAssessmentEntryStatus.AwaitingManagementReview;
  }, [forwardingUpdatedAt]);

  const sectionStyle = useMemo(() => {
    if (isAwaitingEstablishmentDocuments) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Pending];
    }

    if (isAwaitingManagementReview) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Waiting];
    }

    return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Completed];
  }, [isAwaitingEstablishmentDocuments, isAwaitingManagementReview]);

  return (
    <Box className={cn('p-4 flex flex-col gap-4', sectionStyle)}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <p className="text-lg font-semibold">
            Recolha de notas
          </p>

          {isAwaitingEstablishmentDocuments && (
            <p className="text-warning text-xs">
              Aguardando envio de notas
            </p>
          )}

          {isAwaitingManagementReview && (
            <p className="text-warning text-xs">
              Aguardando aprovação
            </p>
          )}
        </div>

        {isAwaitingEstablishmentDocuments && (
          <Button
            operationKlassToCheckPermission="Establishment::Maintenance::FiscalAssessmentDocumentGroups::UpdateDocuments"
            onClick={() => navigate(`/maintenance-workflows/${workflowId}/fiscal-assessment-entry/${forwarding.id}`)}
          >
            Continuar Recolha de notas
          </Button>
        )}
      </div>

      {!isAwaitingEstablishmentDocuments && (
        <div className="flex flex-col gap-3">
          {forwarding?.fiscal_assessment_entry?.document_groups?.map((documentGroup, index) => (
            <React.Fragment key={documentGroup.id}>
              {index > 0 && (
                <div className="h-px bg-white-200" />
              )}

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p>
                      {fiscalAssessmentDocumentGroupsKind[documentGroup.kind] || documentGroup.kind}
                    </p>
                  </div>
                </div>

                {Number(documentGroup.documents.length) > 0 && (
                  <div className="flex flex-col xl:grid xl:grid-cols-2 flex-wrap gap-2">
                    {documentGroup.documents.map((document) => (
                      <FiscalAssessmentDocumentGroupDocument
                        key={document.id}
                        documentGroupId={documentGroup.id}
                        document={document}
                        isAwaitingEstablishmentDocuments={isAwaitingEstablishmentDocuments}
                      />
                    ))}
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </Box>
  )
}

export default MaintenanceWorkflowForwardingFiscalAssessmentEntrySection;
